import type {
	OrderCheckFiringLevel,
	OrderCheckLevel,
} from './order-check-level';
import { isOrderCheckOn } from './order-check-level';

/**
 * PD-16276 — the order-level amount checks: the minimum order total and the
 * maximum manual discount.
 */

export interface MinimumOrderPriceInputModel {
	/** The channel's configured minimum. Null, undefined, 0 or less means none. */
	minimum: number | null | undefined;
	/**
	 * The order's product subtotal, before shipping, tax, fees and discounts.
	 * The minimum is about how much product is ordered, so the storefront's
	 * former comparison against the grand total is reconciled to this.
	 */
	subtotal: number;
}

export interface MinimumOrderPriceFindingModel {
	level: OrderCheckFiringLevel;
	minimum: number;
	subtotal: number;
}

export function checkMinimumOrderPrice(
	input: MinimumOrderPriceInputModel,
	level: OrderCheckLevel | null | undefined,
): MinimumOrderPriceFindingModel | null {
	const minimum = input.minimum;

	if (!isOrderCheckOn(level) || minimum == null || !(minimum > 0)) {
		return null;
	}

	const subtotal = Number.isFinite(input.subtotal) ? input.subtotal : 0;

	return subtotal < minimum ? { level, minimum, subtotal } : null;
}

export type MaximumDiscountType = 'percentage' | 'amount';

export interface MaximumDiscountInputModel {
	/** The channel's configured maximum. Null, undefined, 0 or less means none. */
	maximum: number | null | undefined;
	/** Whether the maximum is a percentage of the subtotal or a dollar amount. */
	type: MaximumDiscountType;
	/** The manual order discount in dollars (promotions are not counted). */
	discountAmount: number;
	/** The order's product subtotal the discount is taken from. */
	subtotal: number;
}

export interface MaximumDiscountFindingModel {
	level: OrderCheckFiringLevel;
	maximum: number;
	type: MaximumDiscountType;
	discountAmount: number;
	/** The discount in the maximum's own unit: percent or dollars. */
	actualDiscount: number;
}

/**
 * How far over the maximum a discount may land before it counts as over.
 * A discount entered as exactly the maximum percentage comes back from the
 * dollar math a hair above it (15.000000001%), and a dollar discount can
 * carry sub-cent rounding; neither is a real excess.
 */
const PERCENTAGE_TOLERANCE = 0.001;
const AMOUNT_TOLERANCE = 0.01;

/**
 * A percentage discount on a zero subtotal is 0%, not infinite: there is
 * nothing to discount.
 *
 * PD-17693: a maximum of 0 means no discount is allowed, so any discount is
 * over it. It used to mean "no limit", which left no way to forbid
 * discounts; a tenant that wants no limit turns the check off. A missing
 * maximum still means the check has nothing to compare against.
 */
export function checkMaximumDiscount(
	input: MaximumDiscountInputModel,
	level: OrderCheckLevel | null | undefined,
): MaximumDiscountFindingModel | null {
	const maximum = input.maximum;

	if (
		!isOrderCheckOn(level) ||
		maximum == null ||
		!(maximum >= 0) ||
		!(input.discountAmount > 0)
	) {
		return null;
	}

	const isPercentage = input.type === 'percentage';
	let actualDiscount = input.discountAmount;

	if (isPercentage) {
		actualDiscount =
			input.subtotal > 0 ? (input.discountAmount / input.subtotal) * 100 : 0;
	}

	// With a maximum of 0 any discount at all is over: the tolerances absorb
	// rounding around a real limit, and would otherwise let a one-cent
	// discount through.
	const tolerance =
		maximum === 0 ? 0 : isPercentage ? PERCENTAGE_TOLERANCE : AMOUNT_TOLERANCE;

	if (!(actualDiscount > maximum + tolerance)) {
		return null;
	}

	return {
		level,
		maximum,
		type: input.type,
		discountAmount: input.discountAmount,
		actualDiscount,
	};
}
